import { Button, Divider } from "@fluentui/react-components";
import Typography from "../../components/Typography";
import { IconButton } from "../../components/IconButton";
import {
  ArrowMaximize24Regular,
  ArrowMinimize24Regular,
  ChatSparkle16Regular,
  ChatSparkle20Regular,
  Dismiss16Regular,
} from "@fluentui/react-icons";
import {
  addMessage,
  setDisplayPromptOverlay,
  setInitialLoad,
  setTopicName,
  toggleAssistantWindow,
} from "../../store/slices/ClimateAssistantSlice";
import { customTokens } from "../../theme/theme";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import logoSmall from "../../assets/images/UNFCCC small logo.svg";
import { useHeaderStyles } from "./AIClimateAssistant.styles";
import { generateMessage } from "./AIClimateAssistant.config";
import { useIsMobile, useSession } from "../../hooks";
import { FC } from "react";

interface IHeader {
  handleScroll: VoidFunction;
  getPromptsList: (query: string) => Promise<void>;
}

const Header: FC<IHeader> = ({ handleScroll, getPromptsList }) => {
  const styles = useHeaderStyles();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobile();

  const { startNewTopic } = useSession();

  const { isExpanded } = useSelector(
    (state: RootState) => state.climateAssistant
  );

  const handleStartNewTopic = () => {
    const user = "bot";
    const text = "";

    const options = {
      hasError: false,
      isNewSession: true,
    };

    const fallbackMsg = generateMessage(user, text, options);

    startNewTopic();
    getPromptsList("");
    dispatch(addMessage(fallbackMsg));
    dispatch(setInitialLoad(false));
    dispatch(setDisplayPromptOverlay(false));
    dispatch(setTopicName(""));

    const timeout = setTimeout(() => {
      handleScroll();
    }, 500);

    return () => clearTimeout(timeout);
  };

  return (
    <>
      {isExpanded && isMobile ? (
        <div className={styles.mobileHeaderContainer}>
          <div className={styles.mobileHeaderTopRow}>
            <Button
              appearance="transparent"
              icon={<ChatSparkle16Regular />}
              className={styles.newChatButton}
              onClick={handleStartNewTopic}
              size="small"
            >
              Start a new topic
            </Button>

            <Button
              appearance="transparent"
              icon={<Dismiss16Regular />}
              size="small"
              onClick={() => dispatch(toggleAssistantWindow(false))}
            >
              Close
            </Button>
          </div>

          <div className={styles.mobileHeaderBottomRow}>
            <img src={logoSmall} alt="UNFCCC logo" className={styles.logo} />

            <div className={styles.headingText}>
              <Typography
                variant="bodyLSemibold"
                mobileVariant="bodyMSemibold"
                color={customTokens.fontDarkGrayBody}
              >
                AI Climate Assistant
              </Typography>

              {isExpanded && (
                <Typography
                  variant="bodyXXRegular"
                  mobileVariant="bodyXXRegular"
                  color={customTokens.colorsNeutralPrimaryGrey6}
                >
                  AI-generated content may be inaccurate
                </Typography>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.header}>
          <img src={logoSmall} alt="UNFCCC logo" className={styles.logo} />

          <div className={styles.headingText}>
            <Typography
              variant="bodyLSemibold"
              mobileVariant="bodyMSemibold"
              color={customTokens.fontDarkGrayBody}
            >
              AI Climate Assistant
            </Typography>

            {isExpanded && (
              <Typography
                variant="bodyXXRegular"
                color={customTokens.colorsNeutralPrimaryGrey6}
              >
                AI-generated content may be inaccurate
              </Typography>
            )}
          </div>

          {isExpanded && (
            <Button
              appearance="transparent"
              icon={<ChatSparkle20Regular />}
              className={styles.newChatButtonDesktop}
              onClick={handleStartNewTopic}
            >
              Start a new topic
            </Button>
          )}

          {isExpanded && <Divider vertical style={{ flexGrow: "unset" }} />}

          <IconButton
            icon={
              isExpanded ? (
                <ArrowMinimize24Regular />
              ) : (
                <ArrowMaximize24Regular />
              )
            }
            ariaLabel={isExpanded ? "collapse-chat" : "expand-chat"}
            onClick={() => dispatch(toggleAssistantWindow(!isExpanded))}
          />
        </div>
      )}
    </>
  );
};

export default Header;
